---
title: The command line
lede: Put a task on the board, follow it until it needs you, and read what it did — from a terminal or a Node script.
order: 35
part: Plan the work
---

`airlock` does from a terminal or a Node script what [the bookmarklet](/integrations) does from a browser. It sends some text and its files (screenshots, logs, PDFs, short videos) to the Airlock running on this computer, which turns them into a task.

Airlock must be open. The command line talks to the app and starts nothing on its own. When the app is closed, it says so and exits with code 2.

### Install it

From the app: **Settings › Add an integration**, then **Install** on the **Command line** row (macOS and Linux; on Windows, use npm). Airlock writes a small script named `airlock` into a folder of your PATH (`~/.local/bin`, `~/bin`, `/opt/homebrew/bin` or `/usr/local/bin`, the first it can write), which runs the command line this version of the app ships — it is always the one that matches the app. **Remove** takes that script back. If no folder of your PATH can be written, it goes to `~/.local/bin` and the page says to add that folder to your PATH; your shell profile is never edited. An `airlock` installed by npm is left alone, and the page says where it is.

Or with npm, also as a dependency of a Node project:

```sh
npm install -g @aircodr/airlock      # the airlock command
npm install @aircodr/airlock         # or as a dependency of a Node project
```

Either way it needs Node 20 or later and has no dependencies.

### Put a task on the board

```sh
airlock task -w Shop "The checkout button does nothing on Safari" --file shot.png --file console.log
```

The task lands in the board's **Backlog**, with the files attached, and waits there until you start it — unless you add `--start`, which puts it in **To do**: the board starts it as soon as there is room.

- **The text** is the task's instruction. Its first line is the title unless you give one with `--title`, of at most 120 characters. A task holds 8,000 characters; for anything longer, put the rest in a file and attach it.
- **The files**: each `--file` (or `-f`) attaches one. A task carries at most 12 files, 10 MB each and 25 MB in all. They are copied into the session when it starts, and the agent is told where they are.
- **The workspace** is required, with `--workspace` (`-w`). It can be given three ways, tried in this order:
  - its **id** on this computer, which stays the same when you rename it;
  - the **id of the organization project** it is a copy of. That id is the same on every computer, so use it in a script your team shares;
  - its **name**, which breaks when the workspace is renamed.

  When several workspaces share a name or a project id, the command refuses and asks for the workspace's own id.

Read the text from another program with `-`, or by piping it in:

```sh
pbpaste | airlock task -w Shop --title "Crash on save"
pnpm test 2>&1 | airlock task - -w prj_8f2c91 --title "Failing tests"
```

### Set it up as the task form does

Every choice of the task form's bar can be given; what you leave out is the workspace's default, except the workflow: without `--workflow`, the task runs as one instruction.

| Option | What it sets |
| --- | --- |
| `--start` | Start it now: it goes to **To do** rather than the Backlog |
| `--placement vm` or `host` | Where it runs: a micro-VM, or this computer |
| `--model <id>` | The model, such as `opus` or `sonnet` |
| `--effort <level>` | An effort the model takes, such as `high` |
| `--target <name>` | A [target](/targets), or `local` |
| `--workflow <name>` | A workflow of yours, or `none` for one instruction |
| `--branch <name>` | The branch every repository is cut from |
| `--repositories a,b` | The repositories bundled, by name; all of them when left out |
| `--urgent` | Start ahead of the queue |

A name that matches nothing is refused, and the message lists what there is.

```sh
airlock task -w Shop "Fix the flaky checkout test" --model sonnet --effort high --placement vm --start
```

### Open the form instead

`--form` opens [the task form](/task-form) in the window, filled in with the text and the files, and writes nothing. You read it, change what you want, and start it or save it, as with the bookmarklet.

```sh
airlock task -w Shop "Crash on save" --file trace.txt --form
```

### List the workspaces

```sh
airlock workspaces
```

This prints one line per workspace: its name, its id, its organization project's id (`-` when it has none) and its repositories. Add `--json` to get the same list, or a task's result, as JSON.

### Write a workflow

```sh
airlock workflows                                   # the library: name, id, steps, local or cloud
airlock workflow get "Fix and review" > flow.json   # one workflow whole, as JSON
airlock workflow save flow.json                     # written back
cat flow.json | airlock workflow save - --workflow wf_8621c264
```

`airlock workflow save` replaces the workflow `--workflow` names (its id or its name), else the one with the file's `"name"`, else it creates one. The file has the shape `get` prints: `{ "name", "steps", "edges" }`, each step with its `id`, `name`, `instruction`, `model`, `effort`, `conversation`, `gate` and `check`, as [the Workflows page](/workflows) draws them. What the page would refuse is refused, in its words. An organization's workflow is changed in the account area.

A step this computer works — a command, a file, an API call — is drawn on the Workflows page, not written from here: `save` keeps one already in the workflow exactly as it is, and refuses to add or change one.

### Merge a session

```sh
airlock sessions                                    # id, status, workspace, task
airlock merge s_4d4fe279 --title "fix: keep the TTL" --body "Written once." --no-pull-request
```

`airlock merge` opens that session's **Merge** dialog in Airlock, on its review, filled with what you gave: `--title`, `--body`, `--branch` (where every repository lands, when the session has not landed yet), `--no-push`, `--no-pull-request`, `--files-only`. You read it and press **Merge** there: nothing lands from the command line. A session is named by its id or by its task's title. Starting a session is `airlock task --start`.

### Take up a ticket, an issue or a pull request

```sh
airlock task -w Shop --ticket AIR-12                                  # a Jira key, on the site of Settings › Integrations
airlock task -w Shop --ticket https://trello.com/c/aBc123 "Safari only"
airlock task -w Shop --issue acme/shop#7
airlock review -w Shop https://github.com/acme/shop/pull/42 --start
```

`--ticket` does what the task form's ticket picker does: Airlock reads the Jira issue or the Trello card, the card takes its title and text and stays linked to it, and the words you give become its notes. A ticket already on the board is refused. `--issue` reads a GitHub issue with your own `gh` (it must be installed and signed in) and makes its title and body the task's words. `airlock review` takes a pull request to be reviewed, as **Review** in the picker does: the repository must be one of the workspace's, on a provider Airlock is signed in to. Both land in the Backlog unless you add `--start`, and both take the task form's options, except that `review` takes no `--branch`.

### Follow the work

```sh
airlock tasks -w Shop --lane backlog        # id, lane, session, title
airlock start t_91c2                        # Start: from the Backlog to To do
airlock status s_4d4fe279                   # working, its title — Fix
airlock wait s_4d4fe279 --timeout 1800      # until it needs you
airlock task -w Shop "Fix the flaky test" --start --wait
airlock events                              # each change, as it happens
```

A task or a session is named by its id or its title. `--lane` takes `backlog`, `todo`, `in-development` or `done` (**Waiting**). `airlock start` also takes `--wait`. `airlock status` says where it stands in one word:

| Word | Means |
| --- | --- |
| `backlog` | On the board, waiting for someone to start it |
| `queued` | In To do, waiting for room |
| `starting` | Its session is booting |
| `working` | The agent is working, or a workflow step is (its name follows) |
| `needs-you` | A gate, a question, a command to approve |
| `review` | The agent is done: its changes are ready to read |
| `merged`, `stopped`, `failed`, `closed` | Over |

`airlock wait` (and `--wait` after `--start`) waits through `queued`, `starting` and `working`, then exits **0** on `review` or `merged`, **3** on `needs-you`, **1** on the rest, and **124** once `--timeout` seconds have passed. `airlock events` prints one line each time a session changes, until you stop it; `--json` makes each line a JSON object, `--session` follows one, `--initial` also prints each session as it stands at the start. Both ask the app every two seconds.

### Read what it did

```sh
airlock report s_4d4fe279                   # each step's report, then the agent's last message
airlock diff s_4d4fe279 | less              # the changes, as a diff
airlock diff s_4d4fe279 --stat
airlock open s_4d4fe279                     # the window on that session, or on a workspace
```

`airlock diff` prints what the review shows, as `git diff` would, each path under its repository's name. Reading it does not move the session to review. What `report` and `diff` print is written by the agent: a control character in it is shown as `^[`, never sent to your terminal.

### What the form offers

```sh
airlock models                              # * marks the default; the efforts each takes
airlock targets -w Shop
airlock repos -w Shop
airlock doctor                              # Node, the data folder, the socket, the app's version
source <(airlock completion zsh)            # or bash, fish
```

### From Node

```js
import { createTask, listWorkspaces, AirlockError } from '@aircodr/airlock';

const task = await createTask({
  text: 'The checkout button does nothing on Safari',
  files: ['shot.png', { name: 'har.json', data: harBuffer }],
  workspace: 'Shop'
});
// → { opened: 'task', id, title, workspaceId, workspace, files: 2, started: false }
```

| Option | What it is |
| --- | --- |
| `text` | The instruction. |
| `title` | Its title; the first line of `text` when left out. |
| `files` | Paths, or `{ name, data, mime? }` where `data` is a Buffer, a Uint8Array or base64. |
| `workspace` | Required. The workspace's id, its organization project's id, or its name. |
| `form` | `true` opens the form instead of writing the task. |
| `start` | `true` puts it in To do, which starts it as soon as there is room. |
| `placement`, `model`, `effort`, `target`, `workflow`, `branch`, `repositories`, `urgent` | As the options above; `repositories` is a list of names. |
| `home` | Airlock's data folder, when it is not `~/.airlock` (`AIRLOCK_HOME`). |

`listSessions()`, `openMerge({ session, title, body, branch, push, pullRequest, filesOnly })`, `listWorkflows()`, `getWorkflow(name)`, `saveWorkflow(doc, { workflow })`, `listTasks(workspace)`, `startTask(name)`, `getStatus(name)`, `waitFor(name, { timeout })`, `watchEvents()` (an async iterator), `getReport(name)`, `getDiff(name, { stat })`, `getChoices(workspace)`, `openInApp(name)`, `about()` and `reviewPull({ workspace, pull })` do what the commands above do; `createTask` also takes `ticket` and `issue`.

A refusal throws an `AirlockError` with the app's own sentence. Its `code` is `USAGE` when no workspace is given, `NOT_RUNNING` when Airlock is closed, `REFUSED` when the app said no (an unknown workspace, a file too large), `PROTOCOL` when the answer could not be read, and `TIMEOUT` when `waitFor` ran out of time.

### Exit codes

| Code | Meaning |
| --- | --- |
| 0 | Done. |
| 1 | Airlock refused the request (the message says why), or `wait` ended other than in review or merged. |
| 2 | Airlock is not running. |
| 3 | `wait` stopped on work that needs you. |
| 64 | The command was not understood, or `--workspace` is missing. |
| 124 | `wait` ran past `--timeout`. |

### What can reach it

The command line reaches the app through a socket in the data folder (a named pipe on Windows). Only your account can open it. Each request also carries a token that the app writes there at every launch and removes at quit. Another account on the computer can do neither. Anything running as you can, as it can already read your files. That is one more reason a task from the command line waits in the backlog until you start it.

On macOS and Linux, a socket's path cannot be longer than 103 bytes. If you moved the data folder somewhere that deep with `AIRLOCK_HOME`, the app opens no socket. The command line then names the path and asks for a shorter folder, and exits with code 1.
